import { memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';

import {
  useAudio,
  useAuth,
  useChatData,
  useConfig
} from '@chainlit/react-client';

import AudioPresence from '@/components/AudioPresence';
import ButtonLink from '@/components/ButtonLink';
import RenewDialog from '@/components/access/RenewDialog';
import { Settings } from '@/components/icons/Settings';
import { Button } from '@/components/ui/button';
import { useSidebar } from '@/components/ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { Translator } from 'components/i18n';

import { chatSettingsSidebarOpenState } from '@/state/project';

import AccessPill, { AccessStrip } from './AccessPill';
import AccountMenu from './AccountMenu';
import ChatProfiles from './ChatProfiles';
import FreshnessPill from './FreshnessPill';
import MobileTabBar from './MobileTabBar';
import NewChatButton from './NewChat';
import NoticesBell from './NoticesBell';
import ReportsNav from './ReportsNav';
import ShareButton from './Share';
import SidebarTrigger from './SidebarTrigger';
import TopicGuideNav from './TopicGuideNav';
import { UserLogo } from './UserLogo';
import { isChatPath, pageTitle, useSections } from './sections';

const Header = memo(() => {
  const { audioConnection } = useAudio();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const nav = useSections();
  const onChat = isChatPath(pathname);
  const title = pageTitle(pathname, nav);
  const { data } = useAuth();
  const { config } = useConfig();
  const { chatSettingsInputs } = useChatData();
  const { open, openMobile, isMobile } = useSidebar();
  const setChatSettingsSidebarOpen = useSetRecoilState(
    chatSettingsSidebarOpenState
  );

  const sidebarOpen = isMobile ? openMobile : open;

  const historyEnabled = data?.requireLogin && config?.dataPersistence;
  const sidebarHidden = config?.ui?.default_sidebar_state === 'hidden';

  const links = config?.ui?.header_links || [];

  const showSettingsInHeader =
    config?.ui?.chat_settings_location === 'sidebar' &&
    chatSettingsInputs.length > 0;

  return (
    <>
      <div
        className="p-3 flex h-[60px] items-center justify-between gap-2 relative w-full max-w-full overflow-hidden"
        id="header"
      >
        {/* min-w-0 is what lets this group shrink below its content width; without
          it a flex child refuses to go narrower than its children and widens the
          page instead. */}
        <div className="flex items-center gap-1 min-w-0 shrink">
          {/* History trigger and New chat belong to the chat only: the other
              sections have no conversation list to open. */}
          {onChat && historyEnabled && !sidebarHidden && !sidebarOpen ? (
            <SidebarTrigger />
          ) : null}
          {onChat && (!(historyEnabled && !sidebarHidden) || !sidebarOpen) ? (
            <NewChatButton navigate={navigate} />
          ) : null}
          {/* The page name. The brand sits in the rail (desktop) and the
              history sidebar (chat), so the header carries only the page. */}
          {title ? (
            <span className="ml-1 truncate text-[16px] font-semibold">
              {title}
            </span>
          ) : null}

          <ChatProfiles navigate={navigate} />
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {audioConnection === 'on' ? (
            <AudioPresence
              type="server"
              height={35}
              width={70}
              barCount={4}
              barSpacing={2}
            />
          ) : null}
          <FreshnessPill />
          <AccessPill />
          <TopicGuideNav />
          <ShareButton />
          {/* The customer's own mark, desktop only. Scheduled reports,
              financial settings, Configurations, API keys, the readme and the
              theme moved into the avatar menu (AccountMenu): the header had
              grown to a row of icons nobody could tell apart, and on a phone
              it did not fit at all. */}
          <span className="hidden sm:contents">
            <UserLogo />
          </span>
          {links &&
            links.map((link, index) => (
              <ButtonLink
                key={`${link.name}-${link.url}-${index}`}
                name={link.name}
                displayName={link.display_name}
                iconUrl={link.icon_url}
                url={link.url}
                target={link.target}
              />
            ))}
          {showSettingsInHeader && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  id="chat-settings-header-button"
                  onClick={() => setChatSettingsSidebarOpen(true)}
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-muted-foreground"
                >
                  <Settings className="!size-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <Translator path="chat.settings.title" />
              </TooltipContent>
            </Tooltip>
          )}
          {/* The notification center and the account menu: in the header on
              phones; on desktop both sit at the bottom of the rail (Rail). */}
          {isMobile ? (
            <>
              <NoticesBell />
              <AccountMenu />
            </>
          ) : null}
        </div>
      </div>
      {/* Hosts the scheduled reports dialog; the avatar menu opens it. */}
      <ReportsNav hideTrigger />
      {/* Phones only: the header row has no room left for the access pill. */}
      <AccessStrip />
      <RenewDialog />
      {/* Phones: the sections as a bottom tab bar. */}
      <MobileTabBar nav={nav} />
    </>
  );
});

export { Header };
